/* What the editor and the people table know about blocks and questions, as plain functions:
   what the sign-in and phone steps are called, each question type's name and limits,
   switching a question's type, what stops a save, and an answer as words. The service checks
   all of it again (agents/onboarding/schemas/flows.py); this is so the owner hears it before
   they press Save. */
import type { AgentUser, Answer, OnboardingBlock, Question, QuestionType } from "@/shared/types";

export const MAX_BLOCKS = 10;
export const MAX_QUESTIONS = 20;

/** The steps a flow holds at most one of: their name on the line, the button that adds one,
    and what it asks of people in one line. */
export const STEPS = {
  login: {
    name: "Ask them to sign in",
    add: "A Definable account. On a channel, they reply YES to link it",
    short: "Signs in with a Definable account. On a channel, they reply YES to link it.",
    says: "Everyone on the client app and the extension is signed in already. Someone on WhatsApp, Telegram or Slack signs in on the agent's page and replies YES in their chat to link it.",
  },
  phone: {
    name: "Verify their phone",
    add: "Coming soon — texting codes isn't switched on yet",
    short: "Gets a code texted to their number.",
    says: "They enter a code texted to their number. A WhatsApp number doesn't count.",
  },
} as const;

/** The block's name on the canvas: its title, or what it is. */
export const stepTitle = (block: OnboardingBlock): string =>
  block.title?.trim() || (block.type === "questions" ? "Questions" : STEPS[block.type].name);

const TEAM_SIZES = ["Just me", "2–10", "11–50", "51 or more"];

/** Questions most flows ask, one press each in the inspector. Added as optional. */
export const QUICK_QUESTIONS: Pick<Question, "label" | "type" | "options">[] = [
  { label: "Email", type: "email" },
  { label: "Phone number", type: "phone" },
  { label: "Company", type: "text" },
  { label: "Job title", type: "text" },
  { label: "Team size", type: "choice", options: TEAM_SIZES },
  { label: "Website", type: "url" },
];

/** Flows to start from when there is none. */
export const TEMPLATES: { name: string; says: string; blocks: OnboardingBlock[] }[] = [
  {
    name: "Contact details",
    says: "First name, last name and email",
    blocks: [{ type: "questions", title: "About you", fields: [
      { label: "First name", type: "text", required: true },
      { label: "Last name", type: "text", required: true },
      { label: "Email", type: "email", required: true },
    ] }],
  },
  {
    name: "Team sign-in",
    says: "Only people with a Definable account",
    blocks: [{ type: "login", accepts: ["definable"] }],
  },
  {
    name: "Qualify a lead",
    says: "Company, role and team size, then sign in",
    blocks: [
      { type: "questions", title: "About your company", fields: [
        { label: "Company", type: "text", required: true },
        { label: "Job title", type: "text", required: false },
        { label: "Team size", type: "choice", required: true, options: TEAM_SIZES },
        { label: "What do you need help with?", type: "long_text", required: false },
      ] },
      { type: "login", accepts: ["definable"] },
    ],
  },
];

/** A new block of the type, as a save sends it: the server gives it its id. */
export function newBlock(type: OnboardingBlock["type"]): OnboardingBlock {
  if (type === "login") return { type, accepts: ["definable"] };
  if (type === "phone") return { type };
  return { type, title: null, fields: [{ label: "", type: "text", required: true }] };
}

export const TYPE_NAMES: Record<QuestionType, string> = {
  text: "Short text",
  long_text: "Long text",
  number: "Number",
  email: "Email",
  phone: "Phone number",
  url: "Web address",
  date: "Date",
  choice: "One choice",
  choices: "Several choices",
  yes_no: "Yes or no",
};

/** What `min`/`max` mean for a type, and the most a text may hold; none for the rest. */
export const LIMITS: Partial<Record<QuestionType, { min: string; max: string; whole: boolean; most?: number }>> = {
  text: { min: "Min characters", max: "Max characters", whole: true, most: 500 },
  long_text: { min: "Min characters", max: "Max characters", whole: true, most: 5000 },
  number: { min: "Lowest", max: "Highest", whole: false },
  choices: { min: "Pick at least", max: "Pick at most", whole: true },
};

export const isChoice = (type: QuestionType) => type === "choice" || type === "choices";

/** The question as another type: options only for choices, limits only where they mean something. */
export function withType(question: Question, type: QuestionType): Question {
  const { options, min, max, ...rest } = question;
  const next: Question = { ...rest, type };
  if (isChoice(type)) next.options = options ?? ["Option 1", "Option 2"];
  // a length still means a length between short and long text; nothing else carries over
  const lengths = (t: QuestionType) => t === "text" || t === "long_text";
  if (lengths(type) && lengths(question.type)) {
    if (min !== undefined) next.min = min;
    if (max !== undefined && max <= LIMITS[type]!.most!) next.max = max;
  }
  return next;
}

/** Every question of the flow, in order; the sign-in and phone steps ask none. */
export const questionsOf = (blocks: OnboardingBlock[]): Question[] =>
  blocks.flatMap((b) => (b.type === "questions" ? b.fields : []));

/** The phone they verified for the flow's phone step: null while they haven't, undefined
    when the flow asks for none. */
export function verifiedPhone(blocks: OnboardingBlock[], proved: AgentUser["proved"] = {}): string | null | undefined {
  const step = blocks.find((b) => b.type === "phone");
  if (!step) return undefined;
  return (step.id && proved[step.id]) || null;
}

function questionProblem(q: Question): string | null {
  const name = q.label.trim() || "A question";
  if (!q.label.trim()) return "Every question needs words.";
  if (isChoice(q.type)) {
    const options = (q.options ?? []).map((o) => o.trim());
    if (!options.length || options.some((o) => !o)) return `${name}: every choice needs words.`;
    if (new Set(options).size !== options.length) return `${name}: two choices are the same.`;
    if (q.type === "choices" && (q.min ?? 0) > options.length) return `${name}: more picks than choices.`;
  }
  const limits = LIMITS[q.type];
  if (limits) {
    for (const bound of [q.min, q.max]) {
      if (bound === undefined) continue;
      if (limits.whole && (!Number.isInteger(bound) || bound < 0)) return `${name}: limits are whole numbers.`;
    }
    if (limits.most !== undefined && (q.max ?? 0) > limits.most) return `${name}: at most ${limits.most} characters.`;
    if (q.min !== undefined && q.max !== undefined && q.min > q.max) return `${name}: the lowest is above the highest.`;
  }
  return null;
}

/** What stops a save, or null. */
export function problem(blocks: OnboardingBlock[]): string | null {
  for (const block of blocks) {
    if (block.type !== "questions") continue;
    if (!block.fields.length) return "A questions block needs at least one question.";
    for (const q of block.fields) {
      const issue = questionProblem(q);
      if (issue) return issue;
    }
  }
  return null;
}

/** An answer as the owner reads it. */
export function said(value: Answer | null | undefined): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) return value.length ? value.join(", ") : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
